import { defineStore } from 'pinia'
import { has, isEmpty } from 'lodash'
import { InAppBrowser } from '@capgo/inappbrowser'
import * as SentryVue from '@sentry/vue'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { api } from '@/services'
import authConstants from '@/constants/auth'
import { usePlatformAuthStore } from '@/stores/platformAuth'
import { useAuthHelpers } from '@/composables'

export const usePlatformAuthFireflyStore = defineStore('platform-auth-firefly', {
    state() {
        return {
            completionStarted: false,
        }
    },
    actions: {
        // This flow is very different to URL auth at the moment
        // We will likely change this when we implement google / new auth methods
        start({ url, schoolCode }) {
            const platformAuthStore = usePlatformAuthStore()

            this.completionStarted = false

            return api.post(url, {
                school_code: schoolCode,
            })
                .then(({ data }) => {
                    if (!has(data, 'url')) {
                        throw new Error('Missing URL from start auth request')
                    }

                    if (!Capacitor.isNativePlatform()) {
                        Browser.open({ url: data.url })
                        return data
                    }

                    const checkInAppBrowserCookies = async () => {
                        const cookies = await InAppBrowser.getCookies({
                            url: data.url,
                            includeHttpOnly: true,
                        })

                        console.log('In-app browser cookies', cookies)

                        // Check for FireflyNETLoggedIn
                        if (!cookies['ASP.NET_SessionId'] || !cookies.SessionSecureA || !cookies.SessionSecureB || cookies.FireflyNETLoggedIn !== 'yes') {
                            return
                        }

                        console.log('Detected auth cookies in in-app browser, closing browser and proceeding')
                        this.completionStarted = true

                        InAppBrowser.close()
                            .catch((e) => {
                                console.log('Unable to close in-app browser after auth', e)
                                SentryVue.captureException(e)
                            })

                        this.login({
                            url: data.login_url,
                            schoolCode,
                            sessionId: cookies['ASP.NET_SessionId'],
                            sessionSecureA: cookies.SessionSecureA,
                            sessionSecureB: cookies.SessionSecureB,
                        })
                    }

                    const closeHandler = () => {
                        InAppBrowser.removeAllListeners()
                        console.log('Firefly auth browser finished')
                        if (this.completionStarted) {
                            return
                        }
                        platformAuthStore.events.emit('abort')
                    }

                    InAppBrowser.addListener('urlChangeEvent', checkInAppBrowserCookies)
                    InAppBrowser.addListener('closeEvent', closeHandler)

                    InAppBrowser.clearCookies({ url: data.url })
                    InAppBrowser.openWebView({
                        url: data.url,
                        title: 'Login',
                    })
                        .catch((e) => {
                            console.log('Unable to open in-app browser from auth', data.url, e)
                            SentryVue.captureException(e)
                        })

                    return data
                })
                .catch((error) => {
                    this.completionStarted = false
                    platformAuthStore.events.emit('error', error)
                })
        },
        login({
            url, schoolCode, sessionId, sessionSecureA, sessionSecureB,
        }) {
            const authHelpers = useAuthHelpers()
            const platformAuthStore = usePlatformAuthStore()

            this.completionStarted = true
            return api.post(url, {
                school_code: schoolCode,
                session_id: sessionId,
                session_secure_a: sessionSecureA,
                session_secure_b: sessionSecureB,
            })
                .then((response) => {
                    authHelpers.handleUserContextChange(response.data)
                    platformAuthStore.events.emit('login', {
                        userSwitched: !isEmpty(response.headers[authConstants.LoginHeaders.userSwitched]),
                    })
                })
                .catch((error) => {
                    this.completionStarted = false
                    platformAuthStore.events.emit('error', error)
                    throw error
                })
        },
    },
})
